import { useEffect, useState, useSyncExternalStore } from "react";

/** One bounded CPU image holds the last complete frame during warm changes.
 * The delay only decides when to SHOW branding; it never releases readiness. */
function createPresentation() {
  let viewer: HTMLElement | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let state = { ready: false, loading: true, holding: false };
  const listeners = new Set<() => void>();
  const publish = (next: typeof state) => {
    state = next;
    listeners.forEach((listener) => listener());
  };
  return {
    snapshot: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    attachViewer: (element: HTMLElement | null) => {
      viewer = element;
    },
    holdCanvas: (element: HTMLCanvasElement | null) => {
      canvas = element;
    },
    onReady: (ready: boolean) => {
      if (state.ready === ready) return;
      clearTimeout(timer);
      timer = undefined;
      if (ready) {
        publish({ ...state, ready: true, holding: false });
        return;
      }
      const source = viewer?.querySelector<HTMLCanvasElement>(
        ".race-renderer--3d:not(.is-hidden) canvas",
      );
      const capture = { target: canvas, captured: false };
      // The scene renders synchronously before copying because WebGL's default
      // preserveDrawingBuffer=false permits already-cleared source pixels.
      source?.dispatchEvent(
        new CustomEvent("race-renderer-capture", { detail: capture }),
      );
      publish({
        ready: false,
        loading: !capture.captured,
        holding: capture.captured,
      });
      if (capture.captured)
        timer = setTimeout(() => {
          timer = undefined;
          if (!state.ready) publish({ ...state, loading: true });
        }, 180);
    },
    dispose: () => {
      clearTimeout(timer);
    },
  };
}

export function usePlayerPresentation() {
  const [controller] = useState(createPresentation);
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.snapshot,
    controller.snapshot,
  );
  useEffect(() => controller.dispose, [controller]);
  return {
    ...state,
    attachViewer: controller.attachViewer,
    holdCanvas: controller.holdCanvas,
    onReady: controller.onReady,
  };
}
